import { serializable, DynamicFormControlLayout, DynamicInputModelConfig, DynamicInputModel, MATCH_VISIBLE } from '@ng-dynamic-forms/core';
import { Model_WorkflowComponent, CODE_WORKFLOW_COMPONENT, TYPE_WORKFLOW_COMPONENT_OBSERVER, TYPE_DATASOURCE, TYPE_DEFAULT_VALUE, EXPRESSION_FIELD_SCOPE } from 'src/app/app.models';
import { ComponentDatasourceProvider, COMPONENT_MODE, COMPONENT_UI } from '../workflow-components.models';
import { WorkflowComponentBuilderOptions } from '../workflow-components.service';

  
export interface CheckboxModelConfig extends DynamicInputModelConfig {
	componentMode: COMPONENT_MODE;
    datas: any[];
    filteredDatas: any[];
    valueField: string;
    displayField: string;
}
  
export class CheckboxModel extends DynamicInputModel {
	@serializable() readonly type: string = 'checkbox';
	componentMode: COMPONENT_MODE;
	componentUI: COMPONENT_UI;
    datas: any[];
    filteredDatas: any[];
    valueField: string;
    displayField: string;

	constructor(config: CheckboxModelConfig, layout?: DynamicFormControlLayout) {
        super(config, layout);
		this.componentMode = config.componentMode;
        this.datas = config.datas;
        this.filteredDatas = config.filteredDatas;
        this.valueField = config.valueField;
        this.displayField = config.displayField;
		this.componentUI = COMPONENT_UI.IONIC;
	}

	static initModel(_component: Model_WorkflowComponent, _datasource: ComponentDatasourceProvider): CheckboxModel {
		const formModel = new CheckboxModel({
			id: _component.ID,
			name: _component.ID,
			label: _component.LABEL,
            datas: _datasource.DATASOURCE_DATAS,
            filteredDatas: _datasource.DATASOURCE_FILTERED_DATAS,
            valueField: _datasource.DATASOURCE_VALUE_FIELD,
            displayField: _datasource.DATASOURCE_DISPLAY_FIELD,
            value: _component.DEFAULT_VALUE,
			required: _component.IS_REQUIRED,
			readOnly: _component.IS_READONLY,
			hidden: _component.IS_VISIBLE !== undefined ? _component.IS_VISIBLE === false : false,
			validators: _component.CUSTOM_VALIDATORS,
			componentMode: _component.COMPONENT_MODE,
			additional: _component,
			relations: _component.RELATIONS
		});
		return formModel;
	}

	static getProperties(_options?: WorkflowComponentBuilderOptions): Model_WorkflowComponent {
		const workflowSteps = !!_options && !!_options.workflow ? _options.workflow.WORKFLOW_STEPS.map(step => ({ ID: step.NUM_WORKFLOWSTEP, TEXT: `${step.INDEX_WORKFLOWSTEP} - ${step.TITRE}` })) : [];
		const workflowComponents = !!_options && !!_options.workflow ? Array.prototype.concat.apply([], _options.workflow.WORKFLOW_STEPS.filter(step => !!step.COMPONENTS).map(step => step.COMPONENTS.map(component => ({ ID: component.ID, TEXT: `${component.INDEX} - ${component.LABEL} [${component.CODE}]`, NUMWORKFLOW_STEP: step.NUM_WORKFLOWSTEP })))) : [];
		const variablesEnvironnement = !!_options && !!_options.variablesEnvironnement ? _options.variablesEnvironnement.map(v => ({ ID: v.NOM_VARIABLE, TEXT: v.LIBELLE_VARIABLE })) : [];

		return {
			ID: 'checkbox',
			CODE: CODE_WORKFLOW_COMPONENT.CHECKBOX,
			ICON: 'checkbox-outline',
			LABEL: 'Checkbox',
			HAS_DATASOURCE: true,
			PROPERTIES: [
				{ ID: 'GUID', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'GUID', IS_VISIBLE: false },
				{ ID: 'INDEX', CODE: CODE_WORKFLOW_COMPONENT.NUMERIC_TEXTBOX, LABEL: 'Index', DEFAULT_VALUE: 1 },
				{ ID: 'ID', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Identifiant', IS_REQUIRED: true, CUSTOM_VALIDATORS: [{ VALIDATOR_NAME: 'no_whitespace' }] },
				{ ID: 'LABEL', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Libellé', IS_REQUIRED: true },
				{ ID: 'IS_VISIBLE', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Est Visible ?', DEFAULT_VALUE: true },
				{ ID: 'IS_REQUIRED', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Est Requis ?' },
				{ ID: 'IS_READONLY', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Lecture Seule ?' },
				{ ID: 'NUM_DATASOURCE', CODE: CODE_WORKFLOW_COMPONENT.DATASOURCE_PICKER, LABEL: 'Source de données', IS_REQUIRED: true },
				{ ID: 'VALUE_COLUMN_NAME', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Valeur', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'DISPLAY_COLUMN_NAME', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Affichage', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'TYPE_DEFAULT_VALUE', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Type Valeur', DEFAULT_VALUE: TYPE_DEFAULT_VALUE.CONSTANT, IS_REQUIRED: true, 
					UIC_PARAMS: {
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [
								{ ID: TYPE_DEFAULT_VALUE.CONSTANT, TEXT: 'Constante' }, 
								{ ID: TYPE_DEFAULT_VALUE.COMPUTED, TEXT: 'Calculé' },
								{ ID: TYPE_DEFAULT_VALUE.ENVIRONMENT_VARIABLE, TEXT: 'Variable d\'environnement' },
								{ ID: TYPE_DEFAULT_VALUE.WORKFLOW_FIELD, TEXT: 'Champ du workflow' },
							],
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					}
				},
				{ ID: 'DEFAULT_VALUE', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Valeur par défaut', RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'TYPE_DEFAULT_VALUE', value: TYPE_DEFAULT_VALUE.CONSTANT }] }] },
				{ 
					ID: 'DEFAULT_VALUE', 
					CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, 
					LABEL: 'Variable d\'environnement', 
					UIC_PARAMS: {
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: variablesEnvironnement,
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					},
					RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'TYPE_DEFAULT_VALUE', value: TYPE_DEFAULT_VALUE.ENVIRONMENT_VARIABLE }] }] 
				},
				{ 
					ID: 'DEFAULT_VALUE_NUMWORKFLOW_STEP', 
					CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, 
					LABEL: 'Etape workflow', 
					IS_REQUIRED: true,
					UIC_PARAMS: {
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: workflowSteps,
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					},
					RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'TYPE_DEFAULT_VALUE', value: TYPE_DEFAULT_VALUE.WORKFLOW_FIELD }] }] 
				},
				{ 
					ID: 'DEFAULT_VALUE_WORKFLOW_COMPONENT_ID', 
					CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, 
					LABEL: 'Champ workflow', 
					IS_REQUIRED: true,
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'DEFAULT_VALUE_NUMWORKFLOW_STEP', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.FILTER_DATASOURCE, params: 'NUMWORKFLOW_STEP' }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: workflowComponents,
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					},
					RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'TYPE_DEFAULT_VALUE', value: TYPE_DEFAULT_VALUE.WORKFLOW_FIELD }] }] 
				},
				{ 
					ID: 'DEFAULT_VALUE_WORKFLOW_COMPONENT_PROPERTY', 
					CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, 
					LABEL: 'Propriété', 
					IS_VISIBLE: false,
					CONDITIONS_VISIBILITY: { condition: 'and', rules: [
						{ field: 'DEFAULT_VALUE_WORKFLOW_COMPONENT_ID', operator: 'eq', scope: EXPRESSION_FIELD_SCOPE.WORKFLOW_COMPONENT_METADATA, value: CODE_WORKFLOW_COMPONENT.DROPDOWN, datasource_column: 'CODE' },
						{ field: 'TYPE_DEFAULT_VALUE', operator: 'eq', scope: EXPRESSION_FIELD_SCOPE.CONSTANT, value: TYPE_DEFAULT_VALUE.WORKFLOW_FIELD }
					]},
					UIC_PARAMS: {
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: workflowComponents,
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					},
				},
			]
		};
	}
}
  